import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "@/lib/api";

/**
 * Two-row brand header used across public, admin, and staff surfaces.
 *
 * Row 1: Full-width grey bar (#5C5C5C) with the label left-aligned in
 *        Trirong, white, wide letter-spacing. An optional `rightSlot`
 *        (e.g. Sign Out button) renders to the right.
 * Row 2: Full-width white section with the SCW logo centred. Logo height
 *        is set via CSS (`.scw-brand-logo`) so it can scale responsively
 *        without inline media queries.
 *
 * Props:
 *   label      Required. "Contact Hub", "Contact Hub · Admin", etc.
 *   homeHref   Where the row-1 label links to. Defaults to "/".
 *   maxWidth   Inner container max-width. Public=1100, Admin=1280.
 *   logoUrl    Optional. If empty, falls back to the cached config logo,
 *              and finally to /logo.svg via <img onError>.
 *   rightSlot  Optional ReactNode rendered on the right of row 1.
 */
export default function BrandHeader({
  label,
  homeHref = "/",
  maxWidth = 1100,
  logoUrl = "",
  rightSlot = null,
  testIdPrefix = "brand",
}) {
  // Pull logo from public config if not explicitly supplied. Cached in
  // memory so navigating between pages doesn't re-flicker the logo.
  const [resolvedLogo, setResolvedLogo] = useState(logoUrl);
  useEffect(() => {
    if (logoUrl) { setResolvedLogo(logoUrl); return; }
    publicApi.config()
      .then((r) => { if (r.data?.logo_url) setResolvedLogo(r.data.logo_url); })
      .catch(() => { /* fall back to /logo.svg */ });
  }, [logoUrl]);

  const logoSrc = resolvedLogo || "/logo.svg";

  return (
    <>
      <div className="scw-brand-row1" data-testid={`${testIdPrefix}-row1`}>
        <div style={{ maxWidth, margin: "0 auto", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <Link to={homeHref} className="scw-brand-label" data-testid={`${testIdPrefix}-label-link`}>
            {label}
          </Link>
          {rightSlot && <div style={{ flexShrink: 0 }}>{rightSlot}</div>}
        </div>
      </div>
      <div className="scw-brand-row2" data-testid={`${testIdPrefix}-row2`}>
        <div style={{ maxWidth, margin: "0 auto", padding: "0 28px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {/* Logo always navigates to the marketing site, consistent with
              the main swisswatch.com.my navigation. Uses a plain anchor
              (not React Router Link) so the customer leaves the contact
              hub entirely. */}
          <a
            href="https://www.swisswatch.com.my/"
            aria-label="Swiss Connection Watches"
            style={{ display: "inline-flex", lineHeight: 0 }}
            data-testid={`${testIdPrefix}-logo-link`}
          >
            <img
              className="scw-brand-logo"
              src={logoSrc}
              alt="Swiss Connection Watches"
              onError={(e) => {
                // If the configured logo URL fails, fall back to the bundled SVG.
                if (e.currentTarget.src.indexOf("/logo.svg") === -1) {
                  e.currentTarget.src = "/logo.svg";
                } else {
                  e.currentTarget.style.display = "none";
                }
              }}
              data-testid={`${testIdPrefix}-logo`}
            />
          </a>
        </div>
      </div>
    </>
  );
}
